import React, { useTransition, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import ErrorBoundary from './components/ErrorBoundary';
import { AllowedNumber } from './types/board';

// Lazy load components
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const HomePage = React.lazy(() => import('./pages/HomePage'));
const SuperAdminPage = React.lazy(() => import('./pages/AdminPage'));
const BoardAdminPage = React.lazy(() => import('./pages/BoardAdminPage'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));

// Loading component
const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <div className="text-gray-600">Loading...</div>
        </div>
    </div>
);

export default function App() {
    const [, startTransition] = useTransition();
    const [user, setUser] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(true);
    const [authChecked, setAuthChecked] = React.useState(false);
    //const [isAuthorized, setIsAuthorized] = React.useState(false);
    const [userRole, setUserRole] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    // Handle authentication state
    React.useEffect(() => {
        const unsubAuth = onAuthStateChanged(auth, (user) => {
            startTransition(() => {
            setUser(user);
                setAuthChecked(true);
            if (!user) {
                setLoading(false);
            }
            });
        }, (_) => {
            startTransition(() => {
                setLoading(false);
                setError('Authentication error. Please refresh the page.');
            });
        });

        return () => unsubAuth();
    }, []);

    // Handle user data fetching
    React.useEffect(() => {
        if (!user) return;

        let unsubNumbers: (() => void) | undefined;

        try {
            unsubNumbers = onSnapshot(collection(db, 'allowedNumbers'), (snapshot) => {
                startTransition(() => {
            const numbers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as AllowedNumber[];
            const currentUser = numbers.find(n => n.phoneNumber === user.phoneNumber);
                    setUserRole(currentUser?.adminLevel || null);
                    setLoading(false);
                    setError(null);
                });
            }, (_) => {
                startTransition(() => {
                    setLoading(false);
                    setError('Failed to load user data. Please refresh the page.');
                });
            });
        } catch (err) {
            startTransition(() => {
            setLoading(false);
                setError('Failed to connect to the database. Please refresh the page.');
        });
        }

        return () => {
            if (unsubNumbers) {
                unsubNumbers();
            }
        };
    }, [user]);

    // Handle timeout
    React.useEffect(() => {
        if (!loading) return; // Don't start timer if not loading
        if (!authChecked) return; // Don't start timer until auth is checked

        const timeoutId = setTimeout(() => {
            setLoading(false);
            setError('Loading took too long. Please refresh the page.');
        }, 30000); // Increased to 30 seconds

        return () => clearTimeout(timeoutId);
    }, [loading, authChecked]);

    const router = createBrowserRouter([
        {
            path: "/",
            element: !user ? (
                <Suspense fallback={<LoadingSpinner />}>
                    <LandingPage />
                </Suspense>
            ) : (
                loading ? <LoadingSpinner /> : (
                    <Suspense fallback={<LoadingSpinner />}>
                        <HomePage />
                    </Suspense>
                )
            )
        },
        {
            path: "/login",
            element: !user ? (
                <Suspense fallback={<LoadingSpinner />}>
                    <LoginPage />
                </Suspense>
            ) : <Navigate to="/" />
        },
        {
            path: "/superadmin",
            element: user ? (
                loading ? <LoadingSpinner /> : (
                    userRole === 'superadmin' ? (
                        <Suspense fallback={<LoadingSpinner />}>
                            <SuperAdminPage />
                        </Suspense>
                    ) : (
                        <div className="flex items-center justify-center h-screen">
                            <div className="text-red-500 p-4 rounded-lg border border-red-200 bg-red-50">
                                Access Denied. You must be a super admin to view this page.
                            </div>
                        </div>
                    )
                )
            ) : <Navigate to="/login" />
        },
        {
            path: "/admin",
            element: user ? (
                loading ? (
                    <div className="flex items-center justify-center h-screen">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <div className="text-gray-600">Loading...</div>
                        </div>
                    </div>
                ) : (
                    (userRole === 'admin' || userRole === 'superadmin') ? (
                        <Suspense fallback={<LoadingSpinner />}>
                            <BoardAdminPage />
                        </Suspense>
                    ) : (
                        <div className="flex items-center justify-center h-screen">
                            <div className="text-red-500 p-4 rounded-lg border border-red-200 bg-red-50">
                                Access Denied. You must be a board admin to view this page.
                            </div>
                        </div>
                    )
                )
            ) : <Navigate to="/login" />
        },
        {
            path: "/admin/:boardId",
            element: user ? (
                loading ? <LoadingSpinner /> : (
                    (userRole === 'admin' || userRole === 'superadmin') ? (
                        <Suspense fallback={<LoadingSpinner />}>
                            <BoardAdminPage />
                        </Suspense>
                    ) : (
                        <div className="flex items-center justify-center h-screen">
                            <div className="text-red-500 p-4 rounded-lg border border-red-200 bg-red-50">
                                Access Denied. You must be a board admin to view this page.
                            </div>
                        </div>
                    )
                )
            ) : <Navigate to="/login" />
        }
    ]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <div className="text-gray-600">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="max-w-md w-full mx-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                        <p className="text-red-700 mb-3">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                        >
                            Refresh Page
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <RouterProvider router={router} />
        </ErrorBoundary>
    );
}
